import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaClock,
  FaWhatsapp,
  FaCheckCircle,
  FaOm,
  FaPray,
} from "react-icons/fa";
import { API_ENDPOINTS } from "../config/api";
import PoojaBookingModal from "../components/PoojaBookingModal";
import "../styles/PoojaDetails.css";
import { resolveImageUrl, getPoojaBackground, DEFAULT_HIGHLIGHT_POOJAS } from "./Poojas";

const API = API_ENDPOINTS.POOJAS;
const WHATSAPP_NUMBER = "916369482332";

const PoojaDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pooja, setPooja] = useState(null);
  const [allPoojas, setAllPoojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  /* ================= FETCH SINGLE POOJA ================= */
  const fetchPooja = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(API);
      const data = await res.json();
      setAllPoojas(data || []);

      const found = (data || []).find((p) => p._id === id) ||
                    DEFAULT_HIGHLIGHT_POOJAS.find((p) => p._id === id || (p.name && p.name.toLowerCase() === (id || "").toLowerCase()));

      if (!found) {
        throw new Error("Pooja not found");
      }

      setPooja(found);
      setActiveImgIndex(0);
    } catch (err) {
      console.error("Error fetching pooja:", err);
      // Fallback to default if match exists
      const fallbackFound = DEFAULT_HIGHLIGHT_POOJAS.find((p) => p._id === id || (p.name && p.name.toLowerCase() === (id || "").toLowerCase()));
      if (fallbackFound) {
        setPooja(fallbackFound);
        setActiveImgIndex(0);
      } else {
        setError("Unable to load pooja details");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPooja();
    window.scrollTo(0, 0);
  }, [fetchPooja]);

  if (loading) {
    return (
      <div className="pooja-details-loading">
        <div className="pooja-spinner" />
        <h2>Loading Pooja Details...</h2>
      </div>
    );
  }

  if (error || !pooja) {
    return (
      <div className="pooja-details-error">
        <h2>{error || "Pooja details not found"}</h2>
        <button className="back-btn" onClick={() => navigate("/poojas")}>
          <FaArrowLeft /> Back to Poojas
        </button>
      </div>
    );
  }

  const fallbackImg = getPoojaBackground(pooja.name);
  const galleryImages = (pooja.images && Array.isArray(pooja.images) && pooja.images.length > 0)
    ? pooja.images
        .filter((img) => typeof img === "string" && img.trim() !== "")
        .map(resolveImageUrl)
    : (fallbackImg ? [fallbackImg] : []);

  const currentImage = galleryImages[activeImgIndex] || galleryImages[0] || fallbackImg || "/images/banner.jpg";

  // WhatsApp Enquiry Link
  const whatsappMessage = encodeURIComponent(
    `Namaste! I would like to enquire and participate in "${pooja.name}" at Sri Gnana Sai Baba Mandir.`
  );
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

  // Other related poojas
  const otherPoojas = (allPoojas.length > 0 ? allPoojas : DEFAULT_HIGHLIGHT_POOJAS)
    .filter((p) => p._id !== pooja._id && p.name !== pooja.name)
    .slice(0, 3);

  return (
    <div className="pooja-details-wrapper">
      <div className="pooja-details-container">

        {/* TOP BAR / BREADCRUMB */}
        <div className="details-nav-bar">
          <button className="back-btn" onClick={() => navigate("/poojas")}>
            <FaArrowLeft /> Back to Poojas
          </button>
          <div className="details-breadcrumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/poojas">Poojas</Link>
            <span>/</span>
            <span className="current">{pooja.name}</span>
          </div>
        </div>

        {/* MAIN SHOWCASE CARD (2-COLUMN COMPACT LAYOUT) */}
        <div className="pooja-showcase-card">

          {/* LEFT: COMPACT MEDIA FRAME */}
          <div className="showcase-media">
            <div className="main-image-frame">
              <img
                src={currentImage}
                alt={pooja.name}
                onError={(e) => {
                  if (fallbackImg && !e.target.src.includes(fallbackImg)) {
                    e.target.src = fallbackImg;
                  }
                }}
              />
              {pooja.time && (
                <div className="media-time-badge">
                  <FaClock /> {pooja.time}
                </div>
              )}
            </div>

            {/* THUMBNAILS (If multiple photos) */}
            {galleryImages.length > 1 && (
              <div className="media-thumbnails">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    className={`thumb-btn ${idx === activeImgIndex ? "active" : ""}`}
                    onClick={() => setActiveImgIndex(idx)}
                    type="button"
                  >
                    <img src={img} alt={`thumb-${idx}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: DETAILS & SIGNIFICANCE */}
          <div className="showcase-info">
            <div className="info-header">
              <span className="ritual-pill">
                <FaOm /> SACRED TEMPLE RITUAL
              </span>
              <h1>{pooja.name}</h1>
            </div>

            <div className="info-meta">
              <div className="meta-item">
                <FaClock className="meta-icon" />
                <div>
                  <span className="meta-label">Timing / Frequency</span>
                  <strong>{pooja.time || "Daily Scheduled Pooja"}</strong>
                </div>
              </div>
            </div>

            <div className="info-divider" />

            <div className="info-description">
              <h3>Significance & Blessings</h3>
              <p>{pooja.desc || "Experience the divine power of timeless Vedic rituals performed with devotion and sacred chants."}</p>
            </div>

            {/* HIGHLIGHT PERKS */}
            <div className="info-perks">
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Traditional Vedic Mantras & Divine Sankalpam</span>
              </div>
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Believed to Remove Obstacles & Bring Peace</span>
              </div>
              <div className="perk-item">
                <FaCheckCircle className="perk-icon" />
                <span>Sacred Prasad & Archana Blessings</span>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="info-actions">
              <button
                type="button"
                className="action-btn-book"
                onClick={() => setIsBookingModalOpen(true)}
              >
                <FaPray className="btn-icon" /> Book This Pooja
              </button>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="action-btn-whatsapp"
              >
                <FaWhatsapp className="btn-icon" /> WhatsApp Enquiry
              </a>
              <button
                className="action-btn-secondary"
                onClick={() => navigate("/poojas")}
                type="button"
              >
                Explore All Rituals
              </button>
            </div>
          </div>

        </div>

        {/* OTHER SACRED RITUALS STRIP */}
        {otherPoojas.length > 0 && (
          <div className="other-rituals-section">
            <div className="other-rituals-header">
              <h2>Other Sacred Temple Rituals</h2>
              <Link to="/poojas" className="view-all-link">View All →</Link>
            </div>

            <div className="other-rituals-grid">
              {otherPoojas.map((item) => {
                const itemBg = (item.images && item.images[0]) 
                  ? resolveImageUrl(item.images[0]) 
                  : (getPoojaBackground(item.name) || "/images/banner.jpg");

                return (
                  <div
                    key={item._id}
                    className="other-ritual-card"
                    onClick={() => navigate(`/poojas/${item._id}`)}
                  >
                    <div className="other-card-img" style={{ backgroundImage: `url("${itemBg}")` }}>
                      {item.time && <span className="other-card-time">{item.time}</span>}
                    </div>
                    <div className="other-card-body">
                      <h4>{item.name}</h4>
                      <p>{(item.desc || "").slice(0, 75)}...</p>
                      <span className="other-read-more">View Ritual →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* POOJA BOOKING & QR PAYMENT MODAL */}
        <PoojaBookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          initialPoojaName={pooja?.name || "Sathya Narayana Pooja"}
          poojaList={allPoojas}
        />

      </div>
    </div>
  );
};

export default PoojaDetails;